import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Bell, Sparkle, Trophy, EnvelopeSimple } from '@phosphor-icons/react';

const CATEGORIES = [
  { id: 'savings', label: 'Savings Milestones', description: 'First $1K, emergency fund progress' },
  { id: 'net_worth', label: 'Net Worth Milestones', description: '$25K, $50K, $100K, $250K, $500K, $1M+' },
  { id: 'retirement', label: 'Retirement Milestones', description: 'Retirement funding targets reached' },
  { id: 'wealth_health', label: 'Wealth Health Score™', description: 'Bronze, Silver, Gold, Platinum, Legacy levels' },
  { id: 'tax_savings', label: 'Tax Savings', description: 'Estimated tax optimization opportunities' },
];

const NotificationSettings = () => {
  const [prefs, setPrefs] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [prefsRes, achRes] = await Promise.all([
        api.get('/notifications/preferences'),
        api.get('/milestones/achievements')
      ]);
      setPrefs(prefsRes.data);
      setAchievements(achRes.data.achievements || []);
    } catch (error) {
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/notifications/preferences', {
        opted_in: prefs.opted_in,
        celebrations_enabled: prefs.celebrations_enabled,
        email_address: prefs.email_address,
        categories: prefs.categories
      });
      toast.success('Notification preferences saved!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (catId) => {
    const updated = prefs.categories.includes(catId)
      ? prefs.categories.filter(c => c !== catId)
      : [...prefs.categories, catId];
    setPrefs({ ...prefs, categories: updated });
  };

  const handleCheckMilestones = async () => {
    try {
      const response = await api.post('/milestones/check');
      if (response.data.new_achievements_count > 0) {
        toast.success(`${response.data.new_achievements_count} new milestone(s) detected!`);
      } else {
        toast.info('No new milestones yet. Keep building!');
      }
      fetchAll();
    } catch (error) {
      toast.error('Failed to check milestones');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96 text-[#4A5568]">Loading...</div>;
  }

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Notifications:</strong> Celebrate your progress with branded milestone emails. We never share personalized tax, legal, or investment advice — only general financial education and motivation.
          </p>
        </div>
      </div>

      <div>
        <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
          Notifications & Achievements
        </h1>
        <p className="text-base text-[#4A5568]">Opt in to milestone celebration emails</p>
      </div>

      {/* Email Status Banner */}
      {!prefs.email_configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="email-status-banner">
          <div className="flex items-start gap-3">
            <EnvelopeSimple size={20} weight="duotone" className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">Email notifications are not configured yet.</p>
              <p className="text-xs text-amber-800 mt-1">Achievements will still be recorded and visible on this page. Email delivery will start once the system is fully configured.</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings */}
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="notification-settings">
          <div className="flex items-center gap-2 mb-4">
            <Bell size={22} weight="duotone" className="text-[#C5A059]" />
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128]">Preferences</h2>
          </div>

          {/* Opt-in toggle */}
          <div className="flex items-center justify-between py-3 border-b border-slate-200">
            <div>
              <p className="font-medium text-[#1A202C]">In-App Celebrations</p>
              <p className="text-xs text-[#4A5568] mt-1">Tasteful confetti + modal when you reach a milestone</p>
            </div>
            <Switch
              checked={prefs.celebrations_enabled ?? true}
              onCheckedChange={(val) => setPrefs({ ...prefs, celebrations_enabled: val })}
              data-testid="celebrations-toggle"
            />
          </div>

          <div className="flex items-center justify-between py-3 border-b border-slate-200">
            <div>
              <p className="font-medium text-[#1A202C]">Email Notifications</p>
              <p className="text-xs text-[#4A5568] mt-1">Receive celebration emails for new milestones</p>
            </div>
            <Switch
              checked={prefs.opted_in}
              onCheckedChange={(val) => setPrefs({ ...prefs, opted_in: val })}
              data-testid="opt-in-toggle"
            />
          </div>

          {/* Email address */}
          <div className="py-4 border-b border-slate-200">
            <Label htmlFor="email_address" className="text-sm">Email Address (optional)</Label>
            <Input
              id="email_address"
              type="email"
              data-testid="notification-email-input"
              placeholder="Defaults to your account email"
              value={prefs.email_address || ''}
              onChange={(e) => setPrefs({ ...prefs, email_address: e.target.value })}
              className="border-slate-300 mt-2"
            />
          </div>

          {/* Categories */}
          <div className="py-4">
            <p className="font-medium text-[#1A202C] mb-3">Notification Categories</p>
            <div className="space-y-3">
              {CATEGORIES.map(cat => (
                <div key={cat.id} className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#1A202C]">{cat.label}</p>
                    <p className="text-xs text-[#4A5568]">{cat.description}</p>
                  </div>
                  <Switch
                    checked={prefs.categories.includes(cat.id)}
                    onCheckedChange={() => toggleCategory(cat.id)}
                    disabled={!prefs.opted_in}
                    data-testid={`category-toggle-${cat.id}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <Button onClick={handleSave} disabled={saving} data-testid="save-preferences-button" className="w-full bg-[#0A1128] hover:bg-[#152145] mt-4">
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>

          <p className="text-xs text-[#4A5568] mt-3 text-center">
            You can unsubscribe anytime via the link in any email.
          </p>
        </div>

        {/* Achievements */}
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="achievements-list">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trophy size={22} weight="duotone" className="text-[#C5A059]" />
              <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128]">Your Achievements</h2>
            </div>
            <Button onClick={handleCheckMilestones} data-testid="check-milestones-button" variant="outline" className="border-[#C5A059] text-[#0A1128]">
              Check Now
            </Button>
          </div>

          {achievements.length === 0 ? (
            <div className="text-center py-12">
              <Trophy size={40} weight="duotone" className="text-slate-300 mx-auto mb-3" />
              <p className="text-[#4A5568] text-sm">No milestones yet</p>
              <p className="text-xs text-[#4A5568] mt-1">Add data across the platform and click "Check Now" to detect achievements</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {achievements.map(ach => (
                <div key={ach.id} className="border border-slate-200 rounded-md p-4 hover:border-[#C5A059] transition-colors" data-testid={`achievement-${ach.milestone_id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <h3 className="font-medium text-[#0A1128]">{ach.title}</h3>
                      <p className="text-xs text-[#4A5568] mt-1">{ach.description}</p>
                    </div>
                    <span className="text-xs text-[#4A5568] whitespace-nowrap">
                      {new Date(ach.achieved_at).toLocaleDateString()}
                    </span>
                  </div>
                  {ach.motivation && (
                    <p className="text-sm text-[#1A202C] mt-2 italic">"{ach.motivation}"</p>
                  )}
                  {ach.email_sent && (
                    <span className="inline-block mt-2 px-2 py-0.5 bg-[#2D6A4F]/10 text-[#2D6A4F] rounded text-xs font-medium">
                      ✓ Email sent
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
